import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { z } from 'zod'
import { useMonthlySummary } from '@/api/reports'
import type { MonthlySummary } from '@/api/reports'
import { categoryLabel } from '@/components/expenses/category-label'
import { LoadingSkeleton } from '@/components/loading-skeleton'
import { formatMoney } from '@/components/materials/pool-format'
import { Card, CardContent } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'

// Monthly summary (UI-SCREENS E-04, 07c #196): one month of cash, read-only. Payments received,
// expenses by category, net cash, then gross margin and — below net cash and outside it — the
// Material waste / loss memo line (owner decisions 2026-09-23, Q2 and #22).
//
// Owner only: revenue figures Staff never see (G2). The nav hides it and the API refuses them.
const searchSchema = z.object({
  month: z.string().optional(),
})

export const Route = createFileRoute('/w/$slug/reports/monthly')({
  component: MonthlySummaryScreen,
  validateSearch: searchSchema,
  staticData: { breadcrumb: 'reports.monthly.title' },
})

function MonthlySummaryScreen() {
  const { t } = useTranslation()
  const { slug } = Route.useParams()
  const { month } = Route.useSearch()
  const navigate = useNavigate({ from: Route.fullPath })
  const summary = useMonthlySummary(slug, month)

  return (
    <section className="flex max-w-2xl flex-col gap-4">
      <div className="flex flex-col gap-1">
        <h1 className="font-heading text-xl font-semibold">{t('reports.monthly.title')}</h1>
        <p className="text-sm text-muted-foreground">{t('reports.monthly.subtitle')}</p>
      </div>

      <div className="flex flex-col gap-2">
        <Label htmlFor="month">{t('reports.monthly.month')}</Label>
        <Input
          id="month"
          type="month"
          className="h-11 sm:max-w-56"
          value={summary.data?.month ?? month ?? ''}
          min={summary.data?.min_month}
          max={summary.data?.max_month}
          onChange={(event) => navigate({ search: { month: event.target.value || undefined } })}
        />
      </div>

      {summary.isPending ? (
        <LoadingSkeleton />
      ) : summary.isError || !summary.data ? (
        <p role="alert" className="text-sm text-destructive">
          {t('errors.generic')}
        </p>
      ) : (
        <Figures summary={summary.data} />
      )}
    </section>
  )
}

function Figures({ summary }: { summary: MonthlySummary }) {
  const { t } = useTranslation()

  return (
    <>
      <Card>
        <CardContent className="flex flex-col gap-3 py-4">
          <Line label={t('reports.monthly.paymentsReceived')} amount={summary.payments_received} />

          <div className="flex flex-col gap-2 border-t pt-3">
            <p className="text-sm font-medium">{t('reports.monthly.expenses')}</p>
            {summary.expenses.categories.length === 0 ? (
              <p className="text-sm text-muted-foreground">{t('reports.monthly.expensesEmpty')}</p>
            ) : (
              summary.expenses.categories.map((row) => (
                <Line key={row.id} label={categoryLabel(t, row.name)} amount={row.amount} muted />
              ))
            )}
            <Line label={t('reports.monthly.expensesTotal')} amount={summary.expenses.total} />
          </div>

          <div className="border-t pt-3">
            <Line label={t('reports.monthly.netCash')} amount={summary.net_cash} strong />
            <p className="text-sm text-muted-foreground">{t('reports.monthly.netCashHint')}</p>
          </div>

          <div className="border-t pt-3">
            <Line label={t('reports.monthly.materialWaste')} amount={summary.material_waste} />
            <p className="text-sm text-muted-foreground">{t('reports.monthly.materialWasteHint')}</p>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="flex flex-col gap-1 py-4">
          <Line label={t('reports.monthly.grossMargin')} amount={summary.gross_margin.amount} strong />
          <p className="text-sm text-muted-foreground">{t('reports.monthly.grossMarginHint')}</p>
          {summary.gross_margin.lines_without_cogs > 0 ? (
            <p className="text-sm text-muted-foreground">
              {t('reports.monthly.grossMarginWithoutCogs', { count: summary.gross_margin.lines_without_cogs })}
            </p>
          ) : null}
        </CardContent>
      </Card>
    </>
  )
}

// Every figure is a whole-Rp integer; a negative one (net cash) keeps its sign before the "Rp".
function Line({ label, amount, muted, strong }: { label: string; amount: number; muted?: boolean; strong?: boolean }) {
  return (
    <div className="flex flex-wrap items-baseline justify-between gap-2">
      <span className={muted ? 'text-sm text-muted-foreground' : 'text-sm'}>{label}</span>
      <span className={strong ? 'font-heading text-lg font-semibold' : 'font-medium'}>
        {amount < 0 ? `-Rp ${formatMoney(-amount)}` : `Rp ${formatMoney(amount)}`}
      </span>
    </div>
  )
}
